/**
 * TeamSystem Impact - Add User Dialog
 * Dialog riutilizzabile per aggiungere utenti/stakeholder interni.
 * Usato da Settings > Utenti e da DMA > Assessment.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { userApi } from '../api/user.api';
import type { Ruolo } from '@teamsystem-impact/shared';

const RUOLI: Ruolo[] = ['amministratore', 'responsabile_esg', 'contributore', 'visualizzatore'];

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddUserDialog({ open, onClose, onSuccess }: AddUserDialogProps) {
  const { t } = useTranslation();

  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [ruolo, setRuolo] = useState<Ruolo>('contributore');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const nomeError = submitted && !nome.trim();
  const cognomeError = submitted && !cognome.trim();
  const emailError = submitted && (!email.trim() || !emailRegex.test(email.trim()));

  const handleClose = () => {
    setNome('');
    setCognome('');
    setEmail('');
    setRuolo('contributore');
    setError(null);
    setSubmitted(false);
    onClose();
  };

  const handleSave = async () => {
    setSubmitted(true);
    setError(null);

    if (!nome.trim() || !cognome.trim() || !email.trim() || !emailRegex.test(email.trim())) {
      return;
    }

    setSaving(true);
    try {
      await userApi.create({ nome: nome.trim(), cognome: cognome.trim(), email: email.trim(), ruolo });
      handleClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || t('settings.users.createError', 'Errore nella creazione utente'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('settings.users.addUser')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            label={t('settings.users.nome')}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            size="small"
            fullWidth
            required
            error={nomeError}
            helperText={nomeError ? t('settings.users.requiredField') : ''}
          />
          <TextField
            label={t('settings.users.cognome')}
            value={cognome}
            onChange={(e) => setCognome(e.target.value)}
            size="small"
            fullWidth
            required
            error={cognomeError}
            helperText={cognomeError ? t('settings.users.requiredField') : ''}
          />
          <TextField
            label={t('settings.users.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            fullWidth
            required
            type="email"
            error={emailError}
            helperText={emailError ? (email.trim() ? t('settings.users.invalidEmail') : t('settings.users.requiredField')) : ''}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t('settings.users.ruoloPiattaforma')}</InputLabel>
            <Select
              value={ruolo}
              label={t('settings.users.ruoloPiattaforma')}
              onChange={(e) => setRuolo(e.target.value as Ruolo)}
            >
              {RUOLI.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`settings.users.roles.${r}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : t('settings.users.addUserSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
